import { Component, OnInit, SkipSelf } from '@angular/core';
import {
  ControlContainer,
  FormGroupDirective,
  Validators,
} from '@angular/forms';
import { ProductDao } from '@app/dao/product/product.dao';
import { ProductElement } from '@app/dao/product/product.type';
import { FormControlOption } from '@app/forms/subscription/definition/interface/formControlOption.interface';
import {
  investmentOptions,
  investmentOptionsWithoutProgrammed,
} from '@app/forms/subscription/services/ref';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { UntilDestroy } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'invest-type',
  templateUrl: './invest-type.component.html',
  styleUrls: ['./invest-type.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: (container: ControlContainer) => container,
      deps: [[new SkipSelf(), ControlContainer]],
    },
  ],
  standalone: false,
})
export class InvestTypeComponent implements OnInit {
  options: FormControlOption[] = investmentOptions;
  productData?: ProductElement;
  constructor(
    private controlContainer: FormGroupDirective,
    private subscriptionFacade: SubscriptionFacadeService,
    private productDao: ProductDao,
  ) {}
  get form() {
    return this.controlContainer.form;
  }
  ngOnInit() {
    const pledge = this.subscriptionFacade.getPledge();
    const product = pledge?.products?.[0];
    const productId = product?.productId;

    if (
      product?.parameters?.partnerFlux === 'CORUM' ||
      (product?.parameters?.partnerFlux === 'SWISSLIFE' &&
        pledge.productType === 'ASSURANCE_VIE')
    ) {
      this.options = investmentOptionsWithoutProgrammed;
      if (
        this.form.get('parameters').get('avInvestmentType').value === 'MONTHLY'
      ) {
        this.form.get('parameters').get('avInvestmentType').setValue('INITIAL');
      }
    }

    if (productId) {
      this.productDao.getProduct('' + productId).subscribe(productData => {
        this.productData = productData;
        this.processValidation(
          this.form?.get('parameters')?.get('avInvestmentType')?.value,
          product,
        );
      });
    } else {
      this.processValidation(
        this.form?.get('parameters')?.get('avInvestmentType')?.value,
        product,
      );
    }

    this.form
      ?.get('parameters')
      ?.get('avInvestmentType')
      .valueChanges.subscribe(data => {
        this.processValidation(data, product);
      });

    this.form
      ?.get('parameters')
      ?.get('avProgammedPaymentType')
      ?.valueChanges.subscribe(() => {
        this.processValidation(
          this.form?.get('parameters')?.get('avInvestmentType')?.value,
          product,
        );
      });
  }

  processValidation(data, product) {
    const minInitialPayment =
      this.productData?.lifeInsuranceMinInitialPayment || 0;

    const avProgammedPaymentType = this.form
      ?.get('parameters')
      ?.get('avProgammedPaymentType')?.value;
    const minMonthlyPayment =
      avProgammedPaymentType === 'QUARTERLY'
        ? this.productData?.lifeInsuranceProgrammedPaymentTrimestriel || 0
        : this.productData?.lifeInsuranceProgrammedPaymentMensuel || 0;

    if (data == 'INITIAL') {
      this.validateInitial(minInitialPayment);
      this.clearMonthly();
    } else if (data == 'MONTHLY') {
      this.validateMonthly(minMonthlyPayment);
      this.clearInitial();
    } else if (data == 'INITIAL_AND_MONTHLY') {
      const isSwisslife = product?.parameters?.partnerFlux === 'SWISSLIFE';
      this.validateInitial(isSwisslife ? minMonthlyPayment : minInitialPayment);
      this.validateMonthly(minMonthlyPayment);
    }
  }

  validateInitial(minValue?) {
    this.form
      ?.get('parameters')
      ?.get('avInitialPayment')
      .setValidators([Validators.required, Validators.min(minValue || 0)]);
    this.form
      ?.get('parameters')
      ?.get('avInitialPayment')
      .updateValueAndValidity();
  }
  validateMonthly(minValue?) {
    this.form
      ?.get('parameters')
      ?.get('avMonthlyPayment')
      .setValidators([Validators.required, Validators.min(minValue || 0)]);
    this.form
      ?.get('parameters')
      ?.get('avMonthlyPayment')
      .updateValueAndValidity();
  }
  clearMonthly() {
    this.form
      ?.get('parameters')
      ?.get('avMonthlyPayment')
      .setValue(null, { emitEvent: false });
    this.form
      ?.get('parameters')
      ?.get('avMonthlyPayment')
      .setValidators([Validators.nullValidator]);
    this.form
      ?.get('parameters')
      ?.get('avMonthlyPayment')
      .updateValueAndValidity();
  }
  clearInitial() {
    this.form?.get('parameters')?.get('avInitialPayment').setValue(
      null,

      { emitEvent: false },
    );
    this.form
      ?.get('parameters')
      ?.get('avInitialPayment')
      .setValidators([Validators.nullValidator]);
    this.form
      ?.get('parameters')
      ?.get('avInitialPayment')
      .updateValueAndValidity();
  }
}
